/**
 * Phase 2b — Public docs layout shell.
 *
 * Owns the 3-column structural grid (left rail · main · right TOC), the docs
 * sub-header, the mobile hamburger overlay, and the data fetch for the nav
 * tree. Children render in the main column.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Menu, X, BookOpen } from "lucide-react";

import Header from "../Header";
import Footer from "../Footer";
import LeftRailNav from "./LeftRailNav";
import DocsSearchBar from "./DocsSearchBar";
import DocsCmdKPalette from "./DocsCmdKPalette";

const API = process.env.REACT_APP_BACKEND_URL;


export default function DocsLayout({ currentSlug, children, rightRail }) {
  const [nav, setNav] = useState([]);
  const [navLoading, setNavLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/wiki/nav`, { withCredentials: true });
        if (!cancelled) setNav(r.data?.nodes || []);
      } catch {
        if (!cancelled) setNav([]);
      } finally {
        if (!cancelled) setNavLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Close drawer on route change.
  useEffect(() => { setDrawerOpen(false); }, [currentSlug]);

  // Cmd+K / Ctrl+K → opens the search palette (Phase 2c).
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const onActivateSearch = () => setPaletteOpen(true);

  return (
    <div className="qt-docs-shell">
      <Header />

      {/* Docs sub-header strip */}
      <div className="qt-docs-subheader">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-3">
          <Link to="/docs" className="flex items-center gap-2 group" data-testid="docs-home-link">
            <BookOpen size={14} style={{ color: "var(--qt-cyan)" }} />
            <span
              className="font-mono text-xs tracking-[0.18em] uppercase"
              style={{ color: "var(--qt-text-2)" }}
            >
              Docs
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="md:hidden p-1.5"
              aria-label="Open docs menu"
              data-testid="docs-mobile-menu-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setDrawerOpen(true);
              }}
              style={{ color: "var(--qt-text-2)" }}
            >
              <Menu size={18} style={{ pointerEvents: "none" }} />
            </button>
          </div>
        </div>
      </div>

      {/* Cmd+K command palette (Phase 2c). */}
      <DocsCmdKPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        navTree={nav}
      />

      <div className="qt-docs-grid max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Left rail (desktop / tablet) */}
        <aside className="qt-docs-leftrail" data-testid="docs-leftrail-desktop">
          {navLoading ? (
            <p className="font-mono text-xs" style={{ color: "#4B5B76", padding: "1rem 0" }}>
              Loading…
            </p>
          ) : (
            <LeftRailNav nodes={nav} currentSlug={currentSlug} />
          )}
        </aside>

        {/* Main column */}
        <main className="qt-docs-main" data-testid="docs-main-column">
          {/* Full-width live-search bar — same width as the wiki content. */}
          <DocsSearchBar onOpenPalette={onActivateSearch} />
          {children}
        </main>

        {/* Right rail (desktop only) */}
        <aside className="qt-docs-rightrail" data-testid="docs-rightrail-desktop">
          {rightRail}
        </aside>
      </div>

      {/* Mobile drawer overlay (left rail) */}
      {drawerOpen && (
        <div
          className="qt-docs-drawer"
          role="dialog"
          aria-modal="true"
          data-testid="docs-mobile-drawer"
        >
          <div className="qt-docs-drawer-panel">
            <div className="flex items-center justify-between mb-6">
              <span
                className="font-mono text-xs tracking-[0.18em] uppercase"
                style={{ color: "var(--qt-cyan)" }}
              >
                Sections
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                data-testid="docs-mobile-drawer-close"
                style={{ color: "var(--qt-text-2)" }}
              >
                <X size={18} style={{ pointerEvents: "none" }} />
              </button>
            </div>
            <LeftRailNav nodes={nav} currentSlug={currentSlug} />
          </div>
          <button
            type="button"
            className="qt-docs-drawer-backdrop"
            aria-label="Close menu backdrop"
            onClick={() => setDrawerOpen(false)}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
